import _ from 'lodash';
import { encode } from 'html-entities';
import TokenizeThis from 'tokenize-this';
import StringBuffer from '@api-consent/util/StringBuffer';

type Context = unknown;

interface Markup2HtmlOptions {
  compatibility?: boolean;
  context?: Context;
}

interface Node {
  type?: string;
  token?: string;
  index?: number;
  children?: Node[];
  variable?: string;
  style?: string;
  url?: string;
}

type Visitor = (node: Node) => boolean | void;

const tokenizer = new TokenizeThis({
  shouldTokenize: [
    '**',
    '\\',
    '~',
    '^',
    '(',
    ')',
    '[',
    ']',
    '|',
    '$$',
    '$$$',
    '$$$$',
    '%%',
    '%%%',
    '%%%%',
    '{{',
    '}}',
    '{{{',
    '}}}',
  ],
  shouldMatch: [], // ['"', '\'']
  shouldDelimitBy: [], // [' ', '\n', '\r', '\t', ':', ','],
  convertLiterals: false, // strings only
});

const tokenize = (str: string): Node[] => {
  if (!str) return [];
  const tokens: Node[] = [];
  tokenizer.tokenize(str, (token, _surroundedBy, index) => {
    if (typeof token !== 'string') return; // should never happen because convertLiterals===false
    tokens.push({ token, index });
  });
  return tokens;
};

const walk = (
  node: Node,
  visitorPre: Visitor,
  visitorPost: Visitor = () => {},
) => {
  if (!visitorPre(node)) return; // stop walking
  const { children = [] } = node;
  children.forEach((child) => walk(child, visitorPre, visitorPost));
  visitorPost(node);
};

const find = (
  nodes: Node[],
  token: string | string[] | undefined,
  start = 0,
) => {
  if (!token) return -1;
  const compare = Array.isArray(token)
    ? (t: string) => token.includes(t)
    : (t: string) => t === token;
  for (let index = start; index < nodes.length; index += 1) {
    const t = nodes[index].token;
    if (t && compare(t)) return index;
  }
  return -1;
};

const parseVariables = (context: Context) => ({ children = [] }: Node) => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos >= 0;) {
    const open = find(childrenOld, ['{{', '{{{'], pos);
    const tokenClose = open >= 0 && childrenOld[open].token === '{{' ? '}}' : '}}}';
    const close = open >= 0 ? find(childrenOld, tokenClose, open + 1) : -1;
    if (open >= 0 && close >= 0) {
      children.push(...childrenOld.slice(pos, open));
      const variable = childrenOld[open + 1]?.token?.trim();
      if (variable && close > open + 1) {
        // exclude empty variable
        const value = `${_.get(context, variable, '') as string}`; // ensure string
        children.push({
          type: 'variable',
          variable,
          children: tokenize(value),
        });
      }
      pos = close + 1;
    } else {
      children.push(...childrenOld.slice(pos));
      pos = -1; // break
    }
  }
  return true; // continue walking
};

const parseBreak = () => ({ children = [] }: Node) => {
  children
    .filter(({ token }) => token === '\\')
    .forEach((child) => {
      child.type = 'break'; // eslint-disable-line no-param-reassign
    });
  return true; // continue walking
};

const parseEnclosing = () => ({ children = [] }: Node) => {
  const types: Record<string, string> = {
    '**': 'emphasis',
    '^': 'superscript',
    '~': 'subscript',
    '%%': 'paragraph:1',
    '%%%': 'paragraph:2',
    '%%%%': 'paragraph:3',
    $$: 'text:1',
    $$$: 'text:2',
    $$$$: 'text:3',
  };
  const tokens = Object.keys(types);
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos >= 0;) {
    const open = find(childrenOld, tokens, pos);
    const token = open >= 0 ? childrenOld[open].token : undefined;
    const close = open >= 0 ? find(childrenOld, token, open + 1) : -1;
    if (token && open >= 0 && close >= 0) {
      const type = types[token];
      const style = (type === 'paragraph:1' || type === 'text:1')
        && childrenOld[open + 1]?.token === '['
        && childrenOld[open + 3]?.token === ']'
        ? childrenOld[open + 2]?.token?.trim() ?? ''
        : undefined;
      children.push(...childrenOld.slice(pos, open));
      children.push({
        type,
        style,
        children: childrenOld.slice(open + (style ? 4 : 1), close),
      });
      pos = close + 1;
    } else {
      children.push(...childrenOld.slice(pos));
      pos = -1; // break
    }
  }
  return true; // continue walking
};

const parseLink = () => ({ children = [] }: Node) => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos >= 0;) {
    const textOpen = find(childrenOld, '[', pos);
    const textClose = textOpen >= 0 ? find(childrenOld, ']', textOpen + 1) : -1;
    const urlOpen = textClose >= 0 && childrenOld[textClose + 1]?.token === '('
      ? textClose + 1
      : -1;
    const urlClose = urlOpen >= 0 ? find(childrenOld, ')', urlOpen + 1) : -1;
    if (textOpen >= 0 && textClose >= 0 && urlOpen >= 0 && urlClose >= 0) {
      children.push(...childrenOld.slice(pos, textOpen));
      children.push({
        type: 'link',
        children: childrenOld.slice(textOpen + 1, textClose),
        url: childrenOld
          .slice(urlOpen + 1, urlClose)
          .map(({ token, children: kids = [] }) => token ?? kids[0].token)
          .join(''),
      });
      pos = urlClose + 1;
    } else if (textOpen >= 0) {
      children.push(childrenOld[pos]);
      pos += 1;
    } else {
      children.push(...childrenOld.slice(pos));
      pos = -1; // break
    }
  }
  return true; // continue walking
};

const parseAnnotation = () => ({ children = [] }: Node) => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos >= 0;) {
    const open = find(childrenOld, '[', pos);
    const separator = open >= 0 ? find(childrenOld, '|', open + 1) : -1;
    const close = open >= 0 && separator >= 0 ? find(childrenOld, ']', separator + 1) : -1;
    if (open >= 0 && separator >= 0 && close >= 0) {
      children.push(...childrenOld.slice(pos, open));
      children.push({
        type: 'annotation',
        children: [
          {
            type: 'annotation:key',
            children: childrenOld.slice(open + 1, separator),
          },
          {
            type: 'annotation:value',
            children: childrenOld.slice(separator + 1, close),
          },
        ],
      });
      pos = close + 1;
    } else {
      children.push(...childrenOld.slice(pos));
      pos = -1; // break
    }
  }
  return true; // continue walking
};

const parse = (str: string, options: Markup2HtmlOptions = {}) => {
  if (!str) return { type: 'root', children: [] };
  const root = { type: 'root', children: tokenize(str) };
  const { context = {} } = options;
  walk(root, parseVariables(context));
  walk(root, parseBreak());
  walk(root, parseEnclosing());
  walk(root, parseLink());
  walk(root, parseAnnotation());
  return root;
};

const mapOpen: Record<string, string> = {
  root: '<div>',
  emphasis: '<em>',
  superscript: '<sup>',
  subscript: '<sub>',
  break: '<br />',
};

const mapClose: Record<string, string> = {
  root: '</div>',
  emphasis: '</em>',
  superscript: '</sup>',
  subscript: '</sub>',
  link: '</a>',
  'paragraph:1': '</div>',
  'paragraph:2': '</div>',
  'paragraph:3': '</div>',
  'text:1': '</span>',
  'text:2': '</span>',
  'text:3': '</span>',
  variable: '</span>',
};

const generatePost = ({
  target,
  compatibility,
}: {
  target: StringBuffer;
  compatibility: boolean;
}) => ({ type }: Node) => {
  if (type === 'variable' && compatibility) return true;
  const tag = type ? mapClose[type] : undefined;
  target.append(tag ?? '');
  return true; // continue walking
};

const generatePre = ({
  target,
  withEncode,
  compatibility,
}: {
  target: StringBuffer;
  withEncode: boolean;
  compatibility: boolean;
}) => ({
  type, token, children = [], url, style, variable,
}: Node) => {
  if (type === 'link') {
    const aHref = url ?? '#';
    const aTarget = aHref && !aHref.startsWith('#') ? ' target="_blank"' : '';
    target.append(`<a href="${aHref}"${aTarget}>`);
  } else if (type === 'annotation') {
    const [annotationKey, annotationValue] = children;
    const annotationKeyHtml = new StringBuffer();
    walk(
      annotationKey,
      generatePre({
        target: annotationKeyHtml,
        withEncode: true,
        compatibility,
      }),
      generatePost({ target: annotationKeyHtml, compatibility }),
    );
    const annotationValueHtml = new StringBuffer();
    walk(
      annotationValue,
      generatePre({
        target: annotationValueHtml,
        withEncode: false,
        compatibility,
      }),
      generatePost({ target: annotationValueHtml, compatibility }),
    );
    target.append(
      `<abbr title="${encode(
        annotationValueHtml.toString(),
      )}">${annotationKeyHtml.toString()}</abbr>`,
    );
    return false; // stop walking --> annotation handled separately
  } else if (type?.startsWith('paragraph')) {
    const match = /paragraph:(?<subtype>\d)/.exec(type);
    const subtype = match?.groups?.subtype ?? '1';
    const clazz = style || `coms-paragraph-${subtype}`;
    target.append(`<div class="${clazz}">`);
  } else if (type?.startsWith('text')) {
    const match = /text:(?<subtype>\d)/.exec(type);
    const subtype = match?.groups?.subtype ?? '1';
    const clazz = style || `coms-text-${subtype}`;
    target.append(`<span class="${clazz}">`);
  } else if (type === 'variable' && compatibility) {
    // do nothing
  } else if (type === 'variable') {
    const clazz = `coms-${variable?.replace(/\W/g, '-') ?? ''}`;
    target.append(`<span class="${clazz}">`);
  } else {
    const tag = type ? mapOpen[type] : undefined;
    const content = tag ?? (withEncode ? encode(token) : token);
    target.append(content ?? '');
  }
  return true; // continue walking
};

export const markup2Html = (
  str: string,
  options: Markup2HtmlOptions = {},
): string => {
  // 1. Texthervorhebung `**wichtig**` --> `<em>wichtig</em>`
  // 2. Zeilenumbruch `\` --> `<br />`
  // 3. Tiefgestellt `O~2~` --> `O<sub>2<sub>`
  // 4. Hochgestellt `x^2^` --> `x<sup>2<sup>`
  // 5. Link `[click here](http://example.com)` --> `<a href="http://example.com" target="_blank">click here</a>`
  //    - es sind nur absolute Links erlaubt
  // 6. Annotation `[o2|o2 Gmbh & Co. OHG]` --> `<abbr title="o2 Gmbh &amp; Co. OHG">o2</abbr>`
  //    - da hier z.B. Tiefstellung vorkommen kann, sollte im Browser nicht die native Darstellung
  //      verwendet werden
  // 7. Paragraph, Text, Style Class
  //    - `%%foo%%` --> `<div class="coms-paragraph-1">foo</div>`
  //    - `$$foo$$` --> `<span class="coms-text-1">foo</span>`
  //    - `%%[foo]bar%%` --> `<div class="foo">bar</div>`
  //    - `$$[foo]bar$$` --> `<span class="foo">bar</span>`
  if (!str) return str;
  const root = parse(str, options);
  // console.log(inspect(root, { depth: null, colors: true })); // debug
  const { compatibility = false } = options;
  const html = new StringBuffer();
  walk(
    root,
    generatePre({ target: html, withEncode: true, compatibility }),
    generatePost({ target: html, compatibility }),
  );
  // console.log(inspect(html, { depth: null, colors: true })); // debug
  return html.toString();
};

/** For backward compatibility reasons, eliminate new markup syntax
 * that are not (yet) supported by older clients. */
export const markupCompatible = (str: string): string => str.replace(/(%{2}|\${2})(\[[\w -]+\])/g, '$1');
